/**
 * Canonical business facts for Thaprachan Thai Cuisine.
 *
 * Every number, URL and opening time here was taken from what the site already
 * publishes (LocationInfo, Footer, the Home page schema). Nothing is invented.
 * Structured data, page metadata and visible content all read from this file so
 * the three can never drift apart — Google treats a mismatch between visible
 * hours and marked-up hours as a quality problem.
 */

/** Canonical host. sitemap.xml uses www, so www is the canonical form. */
export const SITE_URL = 'https://www.thaprachanthaicuisine.com';

export const business = {
  name: 'Thaprachan Thai Cuisine',
  legalHost: SITE_URL,
  telephoneDisplay: '(206) 523-3938',
  telephoneHref: 'tel:+12065233938',
  cuisine: 'Thai',
  priceRange: '$$',
  orderUrl: 'https://order.thaprachanthaicuisine.com/',
  address: {
    street: '805 NE 65th St',
    locality: 'Seattle',
    region: 'WA',
    postalCode: '98115',
    country: 'US',
  },
  geo: {
    latitude: 47.6755887,
    longitude: -122.3185232,
  },
  mapLink: 'https://maps.google.com/?q=805+NE+65th+St,+Seattle,+WA+98115',
  /** Verified profiles linked from the site footer. */
  sameAs: [
    'https://www.instagram.com/thaprachanthai/',
    'https://www.facebook.com/thaprachanthaiofficials/',
    'https://www.yelp.com/biz/thaprachan-thai-cuisine-seattle-5',
  ],
} as const;

export const addressLine = `${business.address.street}, ${business.address.locality}, ${business.address.region} ${business.address.postalCode}`;

/**
 * Hours as shown to customers in LocationInfo. This array is the single copy —
 * LocationInfo imports it, and openingHoursSpecification below is generated
 * from the same values.
 */
export const hoursDisplay = [
  { day: 'Monday', time: '11 AM–3 PM, 4–9 PM' },
  { day: 'Tuesday', time: '11 AM–3 PM, 4–9 PM' },
  { day: 'Wednesday', time: '11 AM–3 PM, 4–9 PM' },
  { day: 'Thursday', time: '11 AM–3 PM, 4–10 PM' },
  { day: 'Friday', time: '11 AM–10 PM' },
  { day: 'Saturday', time: '11 AM–10 PM' },
  { day: 'Sunday', time: '12–9 PM' },
] as const;

/**
 * Machine-readable form of hoursDisplay. Split lunch/dinner service is two
 * intervals per day, which is exactly how Schema.org expects it.
 */
export const openingHoursSpecification = [
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday'],
    opens: '11:00',
    closes: '15:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday'],
    opens: '16:00',
    closes: '21:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: 'Thursday',
    opens: '11:00',
    closes: '15:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: 'Thursday',
    opens: '16:00',
    closes: '22:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Friday', 'Saturday'],
    opens: '11:00',
    closes: '22:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: 'Sunday',
    opens: '12:00',
    closes: '21:00',
  },
];

export const postalAddressSchema = {
  '@type': 'PostalAddress',
  streetAddress: business.address.street,
  addressLocality: business.address.locality,
  addressRegion: business.address.region,
  postalCode: business.address.postalCode,
  addressCountry: business.address.country,
};

/** Stable @id every other entity on the site points back to. */
export const BUSINESS_ID = `${SITE_URL}/#restaurant`;

/**
 * The one canonical business entity. Service and menu-item markup elsewhere
 * references BUSINESS_ID rather than repeating the address, so there is never
 * more than one Restaurant entity describing this address.
 */
export const restaurantSchema = {
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  '@id': BUSINESS_ID,
  name: business.name,
  url: SITE_URL,
  telephone: business.telephoneDisplay,
  image: `${SITE_URL}/assets/images/optimized/ThaprachanLogo.webp`,
  logo: `${SITE_URL}/assets/images/optimized/ThaprachanLogo.webp`,
  servesCuisine: business.cuisine,
  priceRange: business.priceRange,
  address: postalAddressSchema,
  geo: {
    '@type': 'GeoCoordinates',
    latitude: business.geo.latitude,
    longitude: business.geo.longitude,
  },
  hasMap: business.mapLink,
  openingHoursSpecification,
  hasMenu: `${SITE_URL}/menu`,
  sameAs: [...business.sameAs],
  description:
    'Authentic Thai cuisine in north Seattle, serving noodles, curries, Thai street food and desserts near the University of Washington.',
};
